import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Divider, Form, Input, Modal, Spin, Table, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { connect } from 'react-redux';
import moment from 'moment';
import intl from 'react-intl-universal';
import CustomDrawer, { IdrawerRef } from '@/components/CustomDrawer';
import ModalTable, { ImodalTableRef } from '@/components/ModalTable';
import { IcustomColumnType } from '@/components/EditCellTable';
import AuthBtn from '@/components/AuthBtn';
import { forceToRefund } from '@/redux/actions/transaction';
import { IrefundItem } from '@/redux/reducers/transaction';
import { fetchRefundCrossNightItemDetail, fetchRefundItemDetail } from '@/api/bill/refund';
import { fetchRetryRefund } from '@/api/transaction';
import { DATE_TIME_FORMAT, ORDER_TYPE_MAP, OrderType, PERM_CODES } from '@/config';
import { parseFen2Currency } from '@/utils/number';
import { getTimeByUtcOffset } from '@/utils/intl';
import { REFUND_OPERATE_CONTENT_MAP, REFUND_STATUS_MAP } from '../../helper';
import styles from './index.module.scss';

let timer = null;
let count = 9;

const { TextArea } = Input;

const refundColumns: IcustomColumnType<IrefundItem>[] = [
  { title: intl.get('bill_refound_res_pay_channel'), dataIndex: 'paymentTypeName' },
  {
    title: intl.get('bill_refound_res_detail'),
    dataIndex: 'payDetail',
  },
  {
    title: intl.get('bill_refound_res_pay'),
    dataIndex: 'payAmount',
    render: (val) => parseFen2Currency(val),
  },
  {
    title: intl.get('bill_refound_res_pay_time'),
    dataIndex: 'paymentTime',
    render: (val) => (val ? getTimeByUtcOffset(val, DATE_TIME_FORMAT) : ''),
    width: 180,
  },
  {
    title: intl.get('bill_refound_res_refound_amount'),
    dataIndex: 'refundAmount',
    render: (val) => parseFen2Currency(val),
  },
  {
    title: intl.get('bill_refound_res_title'),
    dataIndex: 'refundStatus',
    render: (val, record) => {
      if (val === 1) return intl.get('bill_refound_res_success');
      if (val === 3) {
        return <span style={{ color: '#ff4d4f', wordBreak: 'break-all' }}>{record.reason}</span>;
      }
      return record.reason;
    },
  },
];

const RefundDetailDrawer = ({ myRef, forceToRefundFn }) => {
  const [detail, setDetail] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [remarkText, setRemarkText] = useState('');
  const [forceLoading, setForceLoading] = useState(false);

  const drawerRef = useRef<IdrawerRef>();
  const callbackRef = useRef<any>();

  const modalTableRef = useRef<ImodalTableRef>();
  const [retryLoading, setRetryLoading] = useState(false);

  const intiRefundDetail = async (record) => {
    // 房间订单不含跨夜审调接口/bills/items/refund/detail，其他调/transactions/reception-refund/detail/{no}
    const fetchFn = record.isRoomOrder ? fetchRefundItemDetail : fetchRefundCrossNightItemDetail;
    const params = record.isRoomOrder ? { refundBillItemNo: record.no } : { no: record.no };
    try {
      setLoading(true);
      const res = await fetchFn(params);
      if (res.code === 0) {
        setDetail({ record, ...res.data });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (record, callback?) => {
    drawerRef.current.openDrawer();
    intiRefundDetail(record);
    callbackRef.current = callback;
  };

  const retryRefreshList = () => {
    modalTableRef.current.closeModalTable();
    drawerRef.current.closeDrawer();
    if (typeof callbackRef.current === 'function') {
      callbackRef.current();
    }
  };

  const handleRetry = (record) => {
    if (timer) {
      message.warning(intl.get('bill_refound_res_operate_fast'));
      return;
    }
    timer = window.setInterval(() => {
      count -= 1;
      if (count === 0) {
        clearInterval(timer);
        timer = null;
        count = 9;
      }
    }, 1000);

    // 只有房间订单（非跨夜审存在）
    const { no } = record;
    setRetryLoading(true);
    fetchRetryRefund({ refundBillItemNo: no })
      .then(
        (res) => {
          const { status, reason } = res.data;
          return [status, reason];
        },
        (err) => {
          return [3, err.msg || err.message || intl.get('bill_refound_res_fail')];
        },
      )
      .then(([refundStatus, reason]) => {
        setDetail({
          ...record,
          refundStatus,
          reason,
        });
      })
      .then(() => {
        modalTableRef.current.openModalTable();
      })
      .finally(() => {
        setRetryLoading(false);
      });
  };

  // 手动标记成功
  const handleForceSucceed = () => {
    if (!remarkText) {
      message.error(intl.get('channel_standard_price_input_remark'));
      return;
    }
    setForceLoading(true);
    forceToRefundFn({
      refundBillItemNo: detail?.no,
      remark: remarkText,
    })
      .then(() => {
        setManualOpen(false);
        drawerRef.current.closeDrawer();
        if (typeof callbackRef.current === 'function') {
          callbackRef.current();
        }
      })
      .finally(() => {
        setForceLoading(false);
      });
  };

  const handleForceClose = () => {
    setManualOpen(false);
    setRemarkText('');
  };

  useEffect(() => {
    return () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
        count = 9;
      }
    };
  }, []);

  useImperativeHandle(
    myRef,
    () => ({
      ...drawerRef.current,
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  const columns: ColumnsType<any> = [
    {
      title: intl.get('expenses_operate_content'),
      dataIndex: 'type',
      render: (val) => REFUND_OPERATE_CONTENT_MAP[val],
    },
    { title: intl.get('bill_cross_refound_order_no'), dataIndex: 'refundNo' },
    {
      title: intl.get('bill_cross_operate_result'),
      dataIndex: 'status',
      render: (val) => REFUND_STATUS_MAP[val],
    },
    {
      title: intl.get('manage_employee_process_column_time'),
      dataIndex: 'createTime',
      render: (val) => (val ? moment(val).format(DATE_TIME_FORMAT) : '-'),
    },
    {
      title: intl.get('manage_employee_process_column_user'),
      dataIndex: 'createBy',
      render: (value, record) => `${value || ''} ${record?.createName || ''}`,
    },
    { title: intl.get('reports_ota_table_col16_btn2'), dataIndex: 'remark' },
  ];

  const renderRoomNo = (record) => {
    if (record.orderType === OrderType.goodsSelfService) {
      return record.roomNo;
    }
    if (record.orderType === OrderType.roomBillOrder) {
      if (record.roomNo) {
        return record.roomNo;
      }
      return record.record?.isRoomOrder ? intl.get('bill_refound_res_wait_arrange') : '-';
    }
    return intl.get('bill_refound_res_fronted');
  };

  return (
    <>
      <CustomDrawer
        width="70%"
        className={styles.container}
        title={intl.get('bill_cross_refound_detail')}
        ref={drawerRef}
        footer={
          <>
            {/* 1、订单类型为：房间订单（不含房间订单的跨夜审订单）2、退款结果为：失败 */}
            {detail?.orderType === OrderType.roomBillOrder &&
              !detail?.record?.crossNight &&
              detail.status !== 1 &&
              detail.status !== 2 && (
                <>
                  <AuthBtn>
                    <Button
                      type="primary"
                      loading={retryLoading}
                      onClick={() => handleRetry(detail)}
                    >
                      {intl.get('app_confirm_retry_btn')}
                    </Button>
                    <Divider type="vertical" />
                    <Button
                      onClick={() => setManualOpen(true)}
                      data-authkey={PERM_CODES.MANUAL_MARK_SUCCESSFUL}
                    >
                      {intl.get('bill_refound_res_hand_sign_success_two')}
                    </Button>
                    <Divider type="vertical" />
                  </AuthBtn>
                </>
              )}
            <Button onClick={() => drawerRef.current.closeDrawer()}>
              {intl.get('app_btn_close')}
            </Button>
          </>
        }
      >
        <Spin spinning={loading}>
          <div className="space-between" style={{ margin: '0 0 16px' }}>
            <div className="groupLabel">{intl.get('bill_cross_refound_pay_info')}</div>
          </div>
          <div>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('bill_pay_order_list_payment_order_type')}
            >
              {ORDER_TYPE_MAP[detail?.orderType]}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('bill_refound_res_room_no')}
            >
              {renderRoomNo(detail)}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('bill_refound_res_payer')}
            >
              {detail?.name || '-'}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('bill_refound_res_bill_subject')}
            >
              {detail?.subjectName || '-'}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('bill_refound_res_pay_channel')}
            >
              {detail?.paymentTypeName || '-'}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('bill_refound_res_pay_detail')}
            >
              {detail?.payDetail || '-'}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('reports_cross_refund_col_tit4')}
            >
              {parseFen2Currency(detail?.payAmount) || '-'}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('reports_cross_refund_col_tit7')}
            >
              {detail?.paymentTime
                ? getTimeByUtcOffset(detail?.paymentTime, DATE_TIME_FORMAT)
                : '-'}
            </Form.Item>
          </div>
          <div className="space-between" style={{ margin: '32px 0 16px' }}>
            <div className="groupLabel">{intl.get('bill_cross_refound_infos')}</div>
          </div>
          <div>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('bill_cross_refound_amount')}
            >
              {parseFen2Currency(detail?.refundAmount) || '-'}
            </Form.Item>
            <Form.Item
              labelCol={{ span: 4 }}
              labelAlign="left"
              label={intl.get('roomstate_task_cell_btn_view_refund_result')}
            >
              {REFUND_STATUS_MAP[detail?.status] || '-'}
            </Form.Item>
            {/* 支付渠道 = 中央预订 &  退款结果 = 失败 展示 */}
            {detail?.status === 3 && detail?.crsPayFlag && (
              <Form.Item
                labelCol={{ span: 4 }}
                labelAlign="left"
                label={intl.get('channel_fail_reason')}
              >
                {detail?.refundFailReason || '-'}
              </Form.Item>
            )}
            {
              // 失败
              // 支付渠道	1-wy；2-wy会员卡；3-招联；4-微信；5-支付宝；6-付呗；7-建行；
              detail?.status === 3 && detail?.payChannel === 6 && (
                <div className={styles.tips}>
                  <div style={{ fontWeight: 'bold' }}>
                    {intl.get('bill_cross_refund_fail_explain')}
                  </div>
                  <div>{intl.get('bill_cross_refund_fail_explain1')}</div>
                  <div>{intl.get('bill_cross_refund_fail_explain2')}</div>
                  <div>{intl.get('bill_cross_refund_fail_explain3')}</div>
                </div>
              )
            }
          </div>
        </Spin>

        <div className="space-between" style={{ margin: '32px 0 16px' }}>
          <div className="groupLabel">{intl.get('bill_cross_refound_operate_record')}</div>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={detail?.refundLogs}
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />

        {/* 重试 */}
        <ModalTable
          ref={modalTableRef}
          title={intl.get('bill_refound_res_title')}
          width={1200}
          columns={refundColumns}
          dataSource={[{ ...detail }]}
          loading={false}
          cellTableProps={{ scroll: { x: 800 } }}
          onOk={retryRefreshList}
          onCancel={retryRefreshList}
        >
          <div style={{ marginBottom: '16px' }}>
            {intl.get('bill_refound_res_refund_fail_tips')}
          </div>
        </ModalTable>

        {/* 手动 */}
        <Modal
          title={intl.get('bill_refound_res_hand_sign_success')}
          maskClosable={false}
          open={manualOpen}
          onOk={handleForceSucceed}
          onCancel={handleForceClose}
          okButtonProps={{ loading: forceLoading }}
        >
          <>
            <div style={{ marginTop: 16 }}>
              {intl.get('bill_refound_res_hand_sign_content_info1')}
            </div>
            <div style={{ marginTop: 8, color: 'red' }}>
              {intl.get('bill_refound_res_hand_sign_content_info2')}
            </div>
            <Form.Item label={intl.get('expenses_operate_remark')} style={{ marginTop: 24 }}>
              <TextArea
                placeholder={intl.get('app_required')}
                maxLength={2000}
                showCount
                rows={5}
                value={remarkText}
                onChange={(e) => setRemarkText(e.target.value)}
              />
            </Form.Item>
          </>
        </Modal>
      </CustomDrawer>
    </>
  );
};

const mapStateToProps = (state) => {
  return { ...state.guest, ...state.user, ...state.print };
};

const ConnectComponent: React.FC<any> = connect(mapStateToProps, {
  forceToRefundFn: forceToRefund,
})(React.memo(RefundDetailDrawer));

export default React.forwardRef<any, any>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
